import { Card, CardContent, toast } from '@estac1/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api.js';
import { FlagRow, NumberField } from './billing-form-fields.js';

interface AssistantSettings {
  enabled: boolean;
  keyConfigured: boolean;
  dailyMessagesPerOwner: number;
  monthlyTokensPerLot: number;
  retentionDays: number;
  abuseBlocksPerDay: number;
  triageEnabled: boolean;
}

interface AssistantUsage {
  lots: Array<{
    parkingLotId: string;
    lotName: string | null;
    questions: number;
    blocked: number;
    tokens: number;
  }>;
}

const KEY = ['admin-assistant-settings'];

/**
 * Assistente de suporte do painel (#614): liga e desliga sem deploy, ajusta
 * os limites e mostra quem mais usou no mês. Só responde com a chave da
 * Anthropic no Worker.
 */
export const AssistantCard = () => {
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: KEY,
    queryFn: () => api.request<AssistantSettings>('/v1/admin/assistant/settings'),
  });
  const usage = useQuery({
    queryKey: ['admin-assistant-usage'],
    queryFn: () => api.request<AssistantUsage>('/v1/admin/assistant/usage'),
  });
  const update = useMutation({
    mutationFn: (patch: Partial<AssistantSettings>) =>
      api.request<AssistantSettings>('/v1/admin/assistant/settings', {
        method: 'PUT',
        json: patch,
      }),
    onSuccess: (next) => {
      qc.setQueryData(KEY, next);
      toast.success('Assistente atualizado.');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Falha ao salvar.'),
  });

  const s = settings.data;
  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div>
          <h2 className="text-sm font-semibold">Assistente de suporte</h2>
          <p className="text-xs text-muted-fg">
            O chat do painel que orienta o dono na configuração. Só responde com a chave da
            Anthropic configurada no servidor.
          </p>
        </div>
        {settings.isLoading || !s ? (
          <p className="text-sm text-muted-fg">Carregando...</p>
        ) : (
          <>
            <FlagRow
              label="Assistente no painel"
              hint={
                s.keyConfigured
                  ? 'Chave da Anthropic configurada'
                  : 'Falta a chave ANTHROPIC_API_KEY no Worker'
              }
              enabled={s.enabled}
              pending={update.isPending}
              onToggle={(v) => update.mutate({ enabled: v })}
            />
            <NumberField
              label="Perguntas por dono em 24 h"
              value={s.dailyMessagesPerOwner}
              unit="perguntas"
              min={1}
              max={1000}
              disabled={update.isPending}
              onSave={(v) => update.mutate({ dailyMessagesPerOwner: v })}
            />
            <NumberField
              label="Tokens por estacionamento no mês"
              value={s.monthlyTokensPerLot}
              unit="tokens"
              min={1000}
              max={100_000_000}
              disabled={update.isPending}
              onSave={(v) => update.mutate({ monthlyTokensPerLot: v })}
            />
            <NumberField
              label="Guardar conversas por"
              value={s.retentionDays}
              min={1}
              disabled={update.isPending}
              onSave={(v) => update.mutate({ retentionDays: v })}
            />
            <FlagRow
              label="Triagem diária das perguntas"
              hint="Todo dia às 9h: o que o assistente não resolveu vira tema em Assistente"
              enabled={s.triageEnabled}
              pending={update.isPending}
              onToggle={(v) => update.mutate({ triageEnabled: v })}
            />
            <NumberField
              label="Pausar o dono após tentativas de ataque em 24 h"
              value={s.abuseBlocksPerDay}
              unit="tentativas"
              min={1}
              max={100}
              disabled={update.isPending}
              onSave={(v) => update.mutate({ abuseBlocksPerDay: v })}
            />
          </>
        )}
        {usage.data && usage.data.lots.length > 0 && (
          <ul className="divide-y divide-border text-sm">
            {usage.data.lots.slice(0, 10).map((lot) => (
              <li key={lot.parkingLotId} className="flex justify-between gap-3 py-1.5">
                <span className="truncate">{lot.lotName ?? lot.parkingLotId}</span>
                <span className="shrink-0 tabular-nums text-muted-fg">
                  {lot.questions} perguntas · {lot.blocked} barradas ·{' '}
                  {Math.round(lot.tokens).toLocaleString('pt-BR')} tokens
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
